import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [email, setEmail] = useState(state?.email || "");
  const [remember, setRemember] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={state?.from || "/"} replace />;
  const from = state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password, remember);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#080C14] px-6 overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-blue-500/10" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-blue-500/10" />
      <form data-testid="login-form" onSubmit={submit} className="relative z-10 w-full max-w-sm space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 font-['Outfit'] text-sm font-bold text-white">AFT</div>
          <span className="font-['Outfit'] text-lg font-bold tracking-tight text-slate-100">Freight Command</span>
        </div>
        <div>
          <h1 className="font-['Outfit'] text-4xl font-bold tracking-tight text-slate-100">Sign in</h1>
          <p className="mt-2 text-sm text-slate-500">Use the account your admin created for you.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs text-slate-400">Email</Label>
          <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="border-slate-700/60 bg-slate-900/60 text-slate-100" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs text-slate-400">Password</Label>
          <Input id="password" data-testid="login-password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="border-slate-700/60 bg-slate-900/60 text-slate-100" />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-400">
          <Checkbox data-testid="login-remember-checkbox" checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-slate-600 data-[state=checked]:bg-blue-600" />
          Keep me signed in for 30 days
        </label>
        {state?.notice && <p data-testid="login-notice" className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">{state.notice}</p>}
        {error && <p data-testid="login-error" className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</p>}
        <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 font-semibold text-white hover:bg-blue-700">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />}
          Sign in
        </Button>
      </form>
    </div>
  );
}
